import { useEffect, useRef, useState } from 'react';

interface SpeechRecognition extends EventTarget {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	start(): void;
	stop(): void;
	abort(): void;
	onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
	onend: ((this: SpeechRecognition, ev: Event) => any) | null;
	onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
	onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
}

interface SpeechRecognitionEvent extends Event {
	results: SpeechRecognitionResultList;
	resultIndex: number;
}

type SpeechRecognitionResultList = {
	readonly length: number;
	item(index: number): SpeechRecognitionResult;
	[index: number]: SpeechRecognitionResult;
};

type SpeechRecognitionResult = {
	readonly length: number;
	item(index: number): SpeechRecognitionAlternative;
	[index: number]: SpeechRecognitionAlternative;
	isFinal: boolean;
};

type SpeechRecognitionAlternative = {
	transcript: string;
	confidence: number;
};

interface SpeechRecognitionErrorEvent extends Event {
	error: string;
}

declare global {
	interface Window {
		SpeechRecognition: new () => SpeechRecognition;
		webkitSpeechRecognition: new () => SpeechRecognition;
	}
}

interface SpeechRecognitionCallbacks {
	/** Each finished phrase. */
	onFinalTranscript: (transcript: string) => void;
	/** The words still being spoken, replaced on every result; '' once they settle. */
	onInterimTranscript?: (transcript: string) => void;
	/** stoppedByUser is false when the browser ended the session itself (silence, an error) or it was cancelled. */
	onEnd?: (session: { stoppedByUser: boolean }) => void;
}

export const useSpeechRecognition = (callbacks: SpeechRecognitionCallbacks) => {
	const [isListening, setIsListening] = useState(false);
	const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
	// Set on the start() call, not on onstart, so a second click during the permission prompt stops instead of
	// calling start() again, which throws InvalidStateError.
	const isActiveRef = useRef(false);
	const stoppedByUserRef = useRef(false);
	const callbacksRef = useRef(callbacks);
	callbacksRef.current = callbacks;

	useEffect(() => {
		if (typeof window === 'undefined' || !('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
			return;
		}
		const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
		const speechRecognition = new SpeechRecognitionCtor();

		speechRecognition.continuous = true;
		speechRecognition.interimResults = true;
		speechRecognition.lang = 'en-US';

		speechRecognition.onstart = () => {
			setIsListening(true);
		};

		speechRecognition.onend = () => {
			isActiveRef.current = false;
			setIsListening(false);
			callbacksRef.current.onInterimTranscript?.('');
			callbacksRef.current.onEnd?.({ stoppedByUser: stoppedByUserRef.current });
		};

		speechRecognition.onresult = event => {
			let finalTranscript = '';
			let interimTranscript = '';
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				if (result.isFinal) {
					finalTranscript += result[0]?.transcript ?? '';
				} else {
					interimTranscript += result[0]?.transcript ?? '';
				}
			}
			if (finalTranscript) {
				callbacksRef.current.onFinalTranscript(finalTranscript);
			}
			callbacksRef.current.onInterimTranscript?.(interimTranscript.trim());
		};

		speechRecognition.onerror = event => {
			console.error('Speech recognition error:', event.error);
			isActiveRef.current = false;
			setIsListening(false);
		};

		setRecognition(speechRecognition);

		return () => {
			speechRecognition.onresult = null;
			speechRecognition.onend = null;
			speechRecognition.stop();
		};
	}, []);

	const toggleListening = () => {
		if (!recognition) {
			return;
		}
		if (isActiveRef.current) {
			stoppedByUserRef.current = true;
			recognition.stop();
		} else {
			isActiveRef.current = true;
			stoppedByUserRef.current = false;
			try {
				recognition.start();
			} catch (error) {
				isActiveRef.current = false;
				console.error('Speech recognition failed to start:', error);
			}
		}
	};

	/** Ends the session and drops any phrase still being recognized. */
	const cancelListening = () => {
		if (!recognition || !isActiveRef.current) return;
		stoppedByUserRef.current = false;
		recognition.abort();
	};

	return { isListening, isSupported: recognition != null, toggleListening, cancelListening };
};
